import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Download, Check, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { ProcessingType } from "./ProcessingOptions";

interface ProcessingResultProps {
  result: string;
  type: ProcessingType;
  onBack: () => void;
}

const typeLabels: Record<ProcessingType, string> = {
  extract: 'Extracted Text',
  assignment: 'Assignment Answers',
  presentation: 'Presentation Outline',
  'long-notes': 'Detailed Study Notes',
  'short-notes': 'Quick Revision Notes',
  explain: 'Topic Explanation',
  flashcards: 'Flashcards Generated',
  quiz: 'Quiz Generated',
};

const ProcessingResult = ({ result, type, onBack }: ProcessingResultProps) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = async () => {
    await navigator.clipboard.writeText(result);
    setCopied(true);
    toast({ title: "Copied to clipboard!" });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([result], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `notegenius-${type}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: "Downloaded successfully!" });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <h3 className="font-semibold text-foreground">{typeLabels[type]}</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleCopy}>
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload}>
            <Download className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      <div className="bg-muted/50 rounded-lg p-4 max-h-[60vh] overflow-y-auto">
        <pre className="whitespace-pre-wrap text-sm text-foreground font-sans leading-relaxed">
          {result}
        </pre>
      </div>
    </div>
  );
};

export default ProcessingResult;
